import { useCallback, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTablePreferences } from "./TablePreferencesContext";

// The id a column is stored under. Prefer an explicit `id` on the column
// definition; fall back to its `name`, which is what the CSV exporter already
// treats as a column's identity.
//
// Using the label as the id means RENAMING a column orphans the stored id, and
// the column goes back to hidden for anyone who had customised that table. That
// is the documented behaviour for a new column too, so it degrades consistently —
// but it is the reason to add an explicit `id` to a column whose label you expect
// to reword.
export function columnId(col) {
  if (col == null) return null;
  if (typeof col.id === "string" && col.id.trim()) return col.id.trim();
  if (typeof col.name === "string" && col.name.trim()) return col.name.trim();
  return null;
}

function columnLabel(col) {
  if (typeof col.name === "string" && col.name.trim()) return col.name.trim();
  return columnId(col) || "Untitled column";
}

// ── Applies a user's saved layout to a page's column definitions ──────────────
//
//   const allColumns = [ … ];   // what the page has always declared
//   const { columns, ready, menuProps, customizerProps } =
//     useCustomizableColumns({ listingKey: LISTINGS.ADMIN_TRIAL_LICENSES, columns: allColumns });
//
//   <DataTable columns={columns} … />
//   <TableCustomizerMenu {...menuProps} />
//   <CustomizeTableModal {...customizerProps} />
//
// ── Which columns are in play ────────────────────────────────────────────────
//
// Three kinds of column are NOT customisable, and each for a different reason:
//
//   omit === true      Hidden by a permission check (`omit: !can(…)`). Excluded
//                      from the picker and never rendered — otherwise the picker
//                      would advertise columns the user isn't allowed to see, and
//                      ticking one would appear to do nothing.
//   no resolvable id   Nothing stable to store it under. Always rendered, pinned
//                      ahead of the customisable ones.
//   lockedColumnIds    The page pinned it on purpose. Always rendered, always
//                      first, absent from the picker.
//
// ── What a saved layout means ────────────────────────────────────────────────
//
// Exactly the listed ids, in the listed order. A column added to the page later
// is NOT auto-appended — a user who has arranged their table gets to keep that
// arrangement, and picks up the new column by ticking it in the customiser. Ids
// that no longer match a column are dropped silently.
export default function useCustomizableColumns({
  listingKey,
  columns: allColumns = [],
  lockedColumnIds = [],
}) {
  const { ready, getPreference, savePreference, resetPreference } = useTablePreferences();
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [saving, setSaving] = useState(false);

  const preference = getPreference(listingKey);
  const lockedKey = lockedColumnIds.join("|");

  // Split the page's declarations into the three groups above. `pinned` keeps its
  // declared order; `customizable` is the pool the picker works from.
  const { pinned, customizable } = useMemo(() => {
    const pinnedCols = [];
    const customizableCols = [];
    const locked = new Set(lockedColumnIds);

    for (const col of allColumns) {
      if (!col || col.omit === true) continue;
      const id = columnId(col);
      if (!id || locked.has(id)) {
        pinnedCols.push(col);
      } else {
        customizableCols.push(col);
      }
    }
    return { pinned: pinnedCols, customizable: customizableCols };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns, lockedKey]);

  // What the picker lists, and what "Select all" selects.
  const options = useMemo(
    () => customizable.map((col) => ({ id: columnId(col), label: columnLabel(col) })),
    [customizable]
  );

  // The ids currently visible, in order. With no saved layout this is every
  // customisable column, which is also what the picker opens pre-ticked with.
  const selectedIds = useMemo(() => {
    const available = new Set(options.map((o) => o.id));
    if (!preference) return options.map((o) => o.id);
    const resolved = preference.filter((id) => available.has(id));
    // A saved layout whose ids have ALL disappeared (every column renamed, or the
    // listing key reused for a different table) would blank the table. Fall back
    // to the defaults instead — the user can re-save to clean up the stale row.
    return resolved.length ? resolved : options.map((o) => o.id);
  }, [preference, options]);

  // The array the page hands to <DataTable>.
  const columns = useMemo(() => {
    // Untouched defaults when there is no layout to apply: the page's own order,
    // including anything it deliberately omitted.
    if (!preference) return allColumns.filter((col) => col && col.omit !== true);

    const byId = new Map(customizable.map((col) => [columnId(col), col]));
    const ordered = selectedIds.map((id) => byId.get(id)).filter(Boolean);
    return [...pinned, ...ordered];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns, preference, selectedIds, customizable, pinned]);

  const openCustomizer = useCallback(() => setShowCustomizer(true), []);
  const closeCustomizer = useCallback(() => setShowCustomizer(false), []);

  const handleSave = useCallback(async (nextIds) => {
    setSaving(true);
    try {
      await savePreference(listingKey, nextIds);
      toast.success("Table layout saved.");
      setShowCustomizer(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Could not save table layout.");
    } finally {
      setSaving(false);
    }
  }, [listingKey, savePreference]);

  const handleReset = useCallback(async () => {
    setSaving(true);
    try {
      await resetPreference(listingKey);
      toast.success("Table layout reset to default.");
      setShowCustomizer(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Could not reset table layout.");
    } finally {
      setSaving(false);
    }
  }, [listingKey, resetPreference]);

  return {
    // Hand this to <DataTable columns={…} />
    columns,
    // False while the session's preferences are still loading. Hold the table's
    // render on it so columns don't visibly re-shuffle a moment after paint.
    ready,
    // Whether this user has a saved layout for this listing — drives the "Reset"
    // item in the menu.
    isCustomized: Boolean(preference),
    openCustomizer,
    // Spread onto <TableCustomizerMenu {...menuProps} />
    menuProps: {
      onCustomize: openCustomizer,
      onReset: handleReset,
      isCustomized: Boolean(preference),
      disabled: !ready || saving,
    },
    // Spread onto <CustomizeTableModal {...customizerProps} />
    customizerProps: {
      show: showCustomizer,
      onHide: closeCustomizer,
      options,
      selectedIds,
      isCustomized: Boolean(preference),
      saving,
      onSave: handleSave,
      onReset: handleReset,
    },
  };
}
